// EP-160 T160-201w: Settings -> ความช่วยเหลือจาก FlowRent
// (docs/ep-160-support-access-and-privacy-in-the-app.md §2.1, S2, D1).
// The owner allows the FlowRent team into the account for 1, 7 or 30 days
// with a written instruction, a scope and optionally only some buildings;
// answers a request from the team; withdraws the permission; and reads what
// the team did. An admin sees the status and the history, never the form.
import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { Alert, Badge, Button, Card, Checkbox, ConfirmDialog, Pagination, Radio, Skeleton, Textarea } from '@/components/ui'
import {
  SUPPORT_ACCESS_DURATIONS,
  SUPPORT_ACCESS_SCOPES,
  useCreateSupportAccessGrant,
  useDeclineSupportGrantRequest,
  useRevokeSupportAccessGrant,
  useSupportAccessGrant,
  useSupportSessionHistory,
  useSupportSessions,
  type SupportAccessDuration,
  type SupportAccessGrant,
  type SupportAccessScope,
  type SupportGrantRequest,
  type SupportSessionRow,
} from '@/hooks/useSupportAccess'
import { useAllProperties } from '@/hooks/useProperties'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores/useNotificationStore'

// Mirrors SupportAccessGrant's instruction cap on the server, which stays the
// authority (instruction_too_long).
const INSTRUCTION_MAX_LENGTH = 1000

// Mirrors SupportAccess::OnboardingGrant (D4): the sign-up grant always lasts
// 30 days, and the grant JSON carries no duration of its own.
const ONBOARDING_GRANT_DAYS = 30

// Refusal codes with their own copy under settings.support_access.errors.
const KNOWN_ERRORS = [
  'owner_email_recently_changed',
  'grant_active',
  'request_expired',
  'request_not_pending',
  'invalid_property_ids',
  'instruction_required',
  'instruction_too_long',
  'grant_not_found',
] as const

function errorCode(err: unknown): string | undefined {
  return isAxiosError<{ error?: { code?: string } }>(err) ? err.response?.data?.error?.code : undefined
}

function useSupportAccessErrorMessage() {
  const { t } = useTranslation('settings')
  const translateError = useApiErrorTranslator('settings')
  return (err: unknown) => {
    const code = errorCode(err)
    return code && (KNOWN_ERRORS as readonly string[]).includes(code)
      ? t(`support_access.errors.${code}`)
      : translateError(err)
  }
}

// Whole hours left, rounded up, so a request with 70h 10m left reads 71.
function hoursLeft(expiresAt: string): number {
  return Math.max(1, Math.ceil((Date.parse(expiresAt) - Date.now()) / 3_600_000))
}

export interface SupportAccessCardProps {
  // Owner only: the form, withdrawing and declining. An admin reads.
  canManage: boolean
  // From the mail and push link ?request=<id>.
  requestId?: number | null
}

export function SupportAccessCard({ canManage, requestId }: SupportAccessCardProps) {
  const { t } = useTranslation('settings')
  const { data, isLoading, isError } = useSupportAccessGrant()
  const grant = data?.grant ?? null
  const request = data?.request ?? null
  // The linked request is no longer waiting, and the live grant (if any) is
  // not the answer to it either.
  const deepLinkGone =
    requestId != null && data !== undefined && request?.id !== requestId && grant?.request_id !== requestId

  return (
    <div className="space-y-6">
      <Card title={t('support_access.title')}>
        <div className="space-y-1 text-sm text-base-content/70" data-testid="support-access-intro">
          <p>{t('support_access.intro_1')}</p>
          <p>{t('support_access.intro_2')}</p>
          <p>{t('support_access.intro_3')}</p>
          <p>{t('support_access.intro_4')}</p>
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full mt-4" />
        ) : isError ? (
          <Alert variant="error" className="mt-4">{t('support_access.load_failed')}</Alert>
        ) : (
          <div className="mt-4 space-y-4">
            {!canManage && <Alert variant="info">{t('support_access.admin_read_only')}</Alert>}
            {deepLinkGone && <Alert variant="warning">{t('support_access.request.deep_link_gone')}</Alert>}
            {grant && <GrantStatus grant={grant} canManage={canManage} />}
            {request && <RequestLine request={request} canManage={canManage} />}
            {!grant && !request && !canManage && (
              <p className="text-sm text-base-content/70" data-testid="support-access-none">
                {t('support_access.none_active')}
              </p>
            )}
            {canManage && !grant && <GrantForm request={request} />}
          </div>
        )}
      </Card>

      <SupportHistory />
    </div>
  )
}

function useBuildingsLabel() {
  const { t } = useTranslation('settings')
  const { data: properties } = useAllProperties()
  return (grant: SupportAccessGrant) => {
    if (grant.all_properties) return t('support_access.form.all_buildings')
    const names = grant.property_ids
      .map((id) => properties?.find((p) => p.id === id)?.name)
      .filter((name): name is string => Boolean(name))
    return names.length === grant.property_ids.length
      ? names.join(', ')
      : t('support_access.grant.buildings_count', { count: grant.property_ids.length })
  }
}

function GrantStatus({ grant, canManage }: { grant: SupportAccessGrant; canManage: boolean }) {
  const { t } = useTranslation('settings')
  const formatDateTime = useDateTimeFormatter()
  const buildingsLabel = useBuildingsLabel()
  const errorMessage = useSupportAccessErrorMessage()
  const addToast = useNotificationStore((s) => s.addToast)
  const revoke = useRevokeSupportAccessGrant()
  const [confirming, setConfirming] = useState(false)

  const scope = t(`support_access.scopes.${grant.scope}`)
  const expires = t('support_access.grant.expires', { date: formatDateTime(grant.expires_at) })
  const line =
    grant.source === 'signup'
      ? `${t('support_access.grant.signup_prefix')}: ${scope} · ${t('support_access.form.days', { count: ONBOARDING_GRANT_DAYS })} · ${expires}`
      : `${t('support_access.grant.active_prefix')}: ${scope} · ${buildingsLabel(grant)} · ${expires}`

  const onRevoke = () =>
    revoke.mutate(undefined, {
      onSuccess: () => addToast({ type: 'success', message: t('support_access.grant.revoked') }),
      onError: (err) => addToast({ type: 'error', message: errorMessage(err) }),
      onSettled: () => setConfirming(false),
    })

  return (
    <div className="rounded-lg border border-success/40 bg-success/5 p-3 space-y-2" data-testid="support-access-grant">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium flex items-start gap-2">
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-success" aria-hidden="true" />
          <span>{line}</span>
        </p>
        {canManage && (
          <Button size="sm" variant="outline" onClick={() => setConfirming(true)} data-testid="support-access-revoke">
            {t('support_access.grant.revoke')}
          </Button>
        )}
      </div>
      <p className="text-sm text-base-content/70 break-words">
        {t('support_access.grant.instruction', { text: grant.instruction })}
      </p>
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={onRevoke}
        title={t('support_access.grant.revoke_confirm_title')}
        message={t('support_access.grant.revoke_confirm_body')}
        confirmLabel={t('support_access.grant.revoke_confirm_yes')}
        variant="warning"
        loading={revoke.isPending}
      />
    </div>
  )
}

function RequestLine({ request, canManage }: { request: SupportGrantRequest; canManage: boolean }) {
  const { t } = useTranslation('settings')
  const errorMessage = useSupportAccessErrorMessage()
  const addToast = useNotificationStore((s) => s.addToast)
  const decline = useDeclineSupportGrantRequest()

  const onDecline = () =>
    decline.mutate(request.id, {
      onSuccess: () => addToast({ type: 'success', message: t('support_access.request.declined') }),
      onError: (err) => addToast({ type: 'error', message: errorMessage(err) }),
    })

  return (
    <div className="rounded-lg border border-warning/50 bg-warning/5 p-3 space-y-2" data-testid="support-access-request">
      <p className="text-sm font-medium flex items-start gap-2 break-words">
        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-warning" aria-hidden="true" />
        <span>
          {t('support_access.request.line', {
            name: request.admin_name ?? t('support_access.unknown_staff'),
            reason: request.reason,
            scope: t(`support_access.scopes.${request.suggested_scope}`),
          })}
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-base-content/70">
        {canManage && <span>{t('support_access.form.approve_hint')}</span>}
        <span data-testid="support-access-request-countdown">
          {t('support_access.request.expires_in', { count: hoursLeft(request.expires_at) })}
        </span>
        {canManage && (
          <Button size="sm" variant="outline" onClick={onDecline} loading={decline.isPending}
            data-testid="support-access-decline">
            {t('support_access.request.decline')}
          </Button>
        )}
      </div>
    </div>
  )
}

function GrantForm({ request }: { request: SupportGrantRequest | null }) {
  const { t } = useTranslation('settings')
  const errorMessage = useSupportAccessErrorMessage()
  const addToast = useNotificationStore((s) => s.addToast)
  const create = useCreateSupportAccessGrant()
  const { data: properties = [] } = useAllProperties()
  const [instruction, setInstruction] = useState('')
  // D1: the default scope is always `read`, never the request's suggestion.
  const [scope, setScope] = useState<SupportAccessScope>('read')
  const [days, setDays] = useState<SupportAccessDuration>(1)
  const [someBuildings, setSomeBuildings] = useState(false)
  const [propertyIds, setPropertyIds] = useState<number[]>([])
  const [instructionError, setInstructionError] = useState('')
  const [buildingsError, setBuildingsError] = useState('')

  const toggleProperty = (id: number) => {
    setBuildingsError('')
    setPropertyIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const text = instruction.trim()
    const noText = !text
    const noBuildings = someBuildings && propertyIds.length === 0
    setInstructionError(noText ? t('support_access.form.instruction_required') : '')
    setBuildingsError(noBuildings ? t('support_access.form.buildings_required') : '')
    if (noText || noBuildings) return

    create.mutate(
      {
        scope,
        days,
        instruction: text,
        property_ids: someBuildings ? propertyIds : [],
        ...(request ? { request_id: request.id } : {}),
      },
      {
        onSuccess: () => addToast({ type: 'success', message: t('support_access.grant.created') }),
        onError: (err) => addToast({ type: 'error', message: errorMessage(err) }),
      },
    )
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="support-access-form">
      <Textarea
        id="support-access-instruction"
        label={t('support_access.form.instruction_label')}
        placeholder={t('support_access.form.instruction_placeholder')}
        required
        rows={3}
        maxLength={INSTRUCTION_MAX_LENGTH}
        value={instruction}
        onChange={(e) => {
          setInstruction(e.target.value)
          if (instructionError) setInstructionError('')
        }}
        error={instructionError}
      />

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium mb-1">{t('support_access.form.scope_label')}</legend>
        {SUPPORT_ACCESS_SCOPES.map((value) => (
          <div key={value}>
            <Radio
              name="support-access-scope"
              variant="primary"
              value={value}
              label={t(`support_access.scopes.${value}`)}
              checked={scope === value}
              onChange={() => setScope(value)}
            />
            <p className={`ml-8 text-xs ${value === 'tenants' && scope === 'tenants' ? 'text-warning' : 'text-base-content/60'}`}>
              {t(`support_access.scope_notes.${value}`)}
            </p>
          </div>
        ))}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium mb-1">{t('support_access.form.buildings_label')}</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Radio name="support-access-buildings" variant="primary" label={t('support_access.form.all_buildings')}
            checked={!someBuildings} onChange={() => { setSomeBuildings(false); setBuildingsError('') }} />
          <Radio name="support-access-buildings" variant="primary" label={t('support_access.form.some_buildings')}
            checked={someBuildings} onChange={() => setSomeBuildings(true)} />
        </div>
        {someBuildings && (
          <div className="ml-8 space-y-1" data-testid="support-access-buildings">
            {properties.length === 0 ? (
              <p className="text-sm text-base-content/60">{t('support_access.form.no_buildings')}</p>
            ) : (
              properties.map((p) => (
                <Checkbox key={p.id} size="sm" variant="primary" label={p.name}
                  checked={propertyIds.includes(p.id)} onChange={() => toggleProperty(p.id)} />
              ))
            )}
            {buildingsError && <p className="text-sm text-error" role="alert">{buildingsError}</p>}
          </div>
        )}
      </fieldset>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <fieldset>
          <legend className="text-sm font-medium mb-1">{t('support_access.form.duration_label')}</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {SUPPORT_ACCESS_DURATIONS.map((value) => (
              <Radio key={value} name="support-access-days" variant="primary" value={value}
                label={t('support_access.form.days', { count: value })}
                checked={days === value} onChange={() => setDays(value)} />
            ))}
          </div>
        </fieldset>
        <Button type="submit" loading={create.isPending} data-testid="support-access-submit">
          {t('support_access.form.submit')}
        </Button>
      </div>
    </form>
  )
}

function SupportHistory() {
  const { t } = useTranslation('settings')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useSupportSessions(page)
  const sessions = data?.data ?? []

  return (
    <Card title={t('support_access.history.title')}>
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : isError ? (
        <Alert variant="error">{t('support_access.history.load_failed')}</Alert>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-base-content/70">{t('support_access.history.empty')}</p>
      ) : (
        <ul className="divide-y divide-base-300" data-testid="support-access-history">
          {sessions.map((session) => <SessionItem key={session.id} session={session} />)}
        </ul>
      )}
      {data && data.meta.total_pages > 1 && (
        <Pagination currentPage={page} totalPages={data.meta.total_pages} onPageChange={setPage} />
      )}
    </Card>
  )
}

function SessionItem({ session }: { session: SupportSessionRow }) {
  const { t } = useTranslation('settings')
  const formatDateTime = useDateTimeFormatter()
  const [open, setOpen] = useState(false)
  const name = session.admin_name ?? t('support_access.unknown_staff')

  return (
    <li className="py-3 space-y-2" data-testid={`support-session-${session.id}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="text-sm space-y-1 min-w-0">
          <p>
            <span className="text-base-content/60 whitespace-nowrap mr-2">{formatDateTime(session.started_at)}</span>
            {t('support_access.history.started', { name })}
          </p>
          {session.case && (
            <p className="text-base-content/70 break-words" data-testid={`support-session-case-${session.id}`}>
              {t('support_access.history.case_reason', { reason: session.case.reason })}
              {session.case.ticket_ref && (
                <span className="ml-2 whitespace-nowrap">
                  {t('support_access.history.case_ticket', { ref: session.case.ticket_ref })}
                </span>
              )}
            </p>
          )}
          {session.ended_at ? (
            <p>
              <span className="text-base-content/60 whitespace-nowrap mr-2">{formatDateTime(session.ended_at)}</span>
              {t('support_access.history.ended', { name, count: session.item_count })}
            </p>
          ) : (
            <Badge variant="info" size="sm">{t('support_access.history.in_progress')}</Badge>
          )}
        </div>
        <Button size="sm" variant="ghost" className="self-start" aria-expanded={open}
          onClick={() => setOpen((v) => !v)} data-testid={`support-session-toggle-${session.id}`}>
          {open ? t('support_access.history.hide_details') : t('support_access.history.show_details')}
        </Button>
      </div>
      {open && <SessionDetails sessionId={session.id} />}
    </li>
  )
}

function SessionDetails({ sessionId }: { sessionId: number }) {
  const { t } = useTranslation('settings')
  const formatDateTime = useDateTimeFormatter()
  const { data, isLoading, isError } = useSupportSessionHistory(sessionId, true)

  if (isLoading) return <Skeleton className="h-12 w-full" />
  if (isError || !data) return <Alert variant="error">{t('support_access.history.details_failed')}</Alert>

  const { summary, events } = data.data
  return (
    <div className="rounded-lg bg-base-200/60 p-3 space-y-3 text-sm" data-testid={`support-session-details-${sessionId}`}>
      {summary.items.length === 0 ? (
        <p className="text-base-content/70">{t('support_access.history.no_changes')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {summary.items.map((item) => (
            <Badge key={`${item.action}-${item.resource}`} variant="ghost">
              {t(`support_access.summary.${item.action}`, {
                resource: t(`support_access.summary.resources.${item.resource}`),
                count: item.count,
              })}
            </Badge>
          ))}
        </div>
      )}
      {events.length > 0 && (
        <ul className="space-y-1">
          {events.map((event) => (
            <li key={event.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-base-content/60 whitespace-nowrap">{formatDateTime(event.created_at)}</span>
              <span>{t(`support_access.history.event_${event.action}`, { name: event.actor_name })}</span>
              {/* The resource's name (resource_label), never the raw controller#action. */}
              {event.resource_label && <span className="text-base-content/70">{event.resource_label}</span>}
              {event.subject_count > 0 && (
                <span className="text-base-content/70">
                  {t('support_access.history.event_subjects', { count: event.subject_count })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {data.meta.total_count > events.length && (
        <p className="text-xs text-base-content/60">
          {t('support_access.history.more_events', { shown: events.length, total: data.meta.total_count })}
        </p>
      )}
    </div>
  )
}
